import React, { useState } from "react";
import HandleLogin from "../hooks/handleLogin";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AxiosPostMethod } from "../helpers/customAxios";
import { ErrorToast, SuccessToast } from "../components/toastify";
import { setShowPreloader } from "../store/activePage";
import {
  FaArrowLeft as ArrowLeft,
  FaEnvelope as Mail,
  FaEye as Eye,
  FaEyeSlash as EyeOff,
  FaLock as Lock,
  FaMobileAlt as Smartphone,
  FaUser as User,
  FaQrcode as Qrcode,
} from "react-icons/fa";
import { MdErrorOutline as AlertCircle } from "react-icons/md";

const forgotPasswordEndpoints = {
  sendOtp: "customer/forgot-password/send-otp",
  reset: "customer/forgot-password/reset",
};

const CustomerLogin = () => {
  const Logo = require("../assets/images/logo.jpeg");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [otpRequested, setOtpRequested] = useState(false);
  const [resetUsername, setResetUsername] = useState("");
  const [maskedMobile, setMaskedMobile] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required("Email or Mobile is required"),
      password: Yup.string().required("Password is required"),
    }),
    onSubmit: (values) => {
      HandleLogin(dispatch, navigate, values);
    },
  });

  const requestOtpFormik = useFormik({
    initialValues: {
      username: "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required("Email or Mobile is required"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      dispatch(setShowPreloader(true));
      const res = await AxiosPostMethod(
        forgotPasswordEndpoints.sendOtp,
        { username: values.username },
        true,
      );
      dispatch(setShowPreloader(false));
      setSubmitting(false);

      if (res?.status === 200) {
        setResetUsername(values.username);
        setMaskedMobile(res?.data?.data?.mobile || "");
        setOtpRequested(true);
        SuccessToast(res?.data?.message || "OTP sent successfully");
        return;
      }

      ErrorToast(res?.data?.message || "Unable to send OTP");
    },
  });

  const resetPasswordFormik = useFormik({
    initialValues: {
      otp: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      otp: Yup.string()
        .length(6, "OTP must be exactly 6 digits")
        .matches(/^\d{6}$/, "OTP must only contain numbers")
        .required("OTP is required"),
      newPassword: Yup.string()
        .min(6, "New password must be at least 6 characters")
        .required("New password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], "Passwords must match")
        .required("Please confirm your new password"),
    }),
    onSubmit: async (values, { resetForm, setSubmitting, setFieldError }) => {
      dispatch(setShowPreloader(true));
      const res = await AxiosPostMethod(
        forgotPasswordEndpoints.reset,
        {
          username: resetUsername,
          otp: values.otp,
          new_password: values.newPassword,
        },
        true,
      );
      dispatch(setShowPreloader(false));
      setSubmitting(false);

      if (res?.status === 200) {
        SuccessToast(res?.data?.message || "Password reset successfully");
        resetForm();
        requestOtpFormik.resetForm();
        setShowForgotPassword(false);
        setOtpRequested(false);
        setResetUsername("");
        setMaskedMobile("");
        return;
      }

      if (res?.status === 400) {
        setFieldError("otp", res?.data?.message || "Invalid OTP");
      }
      ErrorToast(res?.data?.message || "Unable to reset password");
    },
  });

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setOtpRequested(false);
    setResetUsername("");
    setMaskedMobile("");
    requestOtpFormik.resetForm();
    resetPasswordFormik.resetForm();
  };

  return (
    <div className="flex min-h-screen w-full bg-gray-50">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-secondary to-secondary/90 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0 opacity-10">
          <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#grid)" />
          </svg>
        </div>
        <div className="relative z-10 flex flex-col justify-center items-center w-full px-16 text-white">
          <div className="mb-8 p-8 bg-white/10 backdrop-blur-sm rounded-2xl">
            <img src={Logo} alt="Logo" className="w-24 h-24 object-contain" />
          </div>
          <h1 className="text-4xl font-bold mb-4 text-center">Customer Portal</h1>
        
          <div className="mt-12 flex gap-8 text-sm text-white/60">
            <div className="text-center">
              <div className="text-2xl font-bold text-white">10K+</div>
              <div>Events</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">50K+</div>
              <div>Happy Customers</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">100+</div>
              <div>Cities</div>
            </div>
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-white/5 rounded-full"></div>
        <div className="absolute -top-32 -right-32 w-80 h-80 bg-white/5 rounded-full"></div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <img src={Logo} alt="Logo" className="h-16" />
          </div>

          {!showForgotPassword ? (
            <form onSubmit={formik.handleSubmit} className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Welcome back</h2>
                <p className="mt-2 text-gray-500">Enter your credentials to access your account</p>
              </div>

              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                  Email or Mobile
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formik.values.username}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`block w-full pl-12 pr-4 py-3.5 bg-white border rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder-gray-400 ${
                      formik.touched.username && formik.errors.username
                        ? "border-red-300 focus:ring-red-500/20"
                        : "border-gray-200"
                    }`}
                    placeholder="Enter email or mobile"
                    autoComplete="username"
                  />
                </div>
                {formik.touched.username && formik.errors.username && (
                  <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {formik.errors.username}
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      requestOtpFormik.setFieldValue("username", formik.values.username);
                      setShowForgotPassword(true);
                    }}
                    className="text-sm text-primary hover:text-primary/80 font-medium"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`block w-full pl-12 pr-12 py-3.5 bg-white border rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder-gray-400 ${
                      formik.touched.password && formik.errors.password
                        ? "border-red-300 focus:ring-red-500/20"
                        : "border-gray-200"
                    }`}
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                {formik.touched.password && formik.errors.password && (
                  <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {formik.errors.password}
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl transition-all transform hover:scale-[1.02] active:scale-[0.98]"
              >
                Sign In
              </button>

              <div className="relative flex py-1 items-center">
                <div className="flex-grow border-t border-gray-200"></div>
                <span className="flex-shrink mx-4 text-gray-400 text-xs font-bold uppercase tracking-wider">Or</span>
                <div className="flex-grow border-t border-gray-200"></div>
              </div>

              <Link
                to="/scan-qr"
                className="w-full flex items-center justify-center gap-2.5 py-3.5 px-4 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-amber-600/15 text-decoration-none"
              >
                <Qrcode size={16} />
                Create Quick Token (Scan QR)
              </Link>

              <p className="text-center text-sm text-gray-600">
                Don't have an account?{" "}
                <Link to={"/register"} className="text-primary hover:text-primary/80 font-semibold">
                  Register Now
                </Link>
              </p>
            </form>
          ) : (
            <form
              onSubmit={
                otpRequested
                  ? resetPasswordFormik.handleSubmit
                  : requestOtpFormik.handleSubmit
              }
              className="space-y-6"
            >
              <button
                type="button"
                onClick={closeForgotPassword}
                className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to login
              </button>

              <div>
                <h2 className="text-3xl font-bold text-gray-900">Reset password</h2>
                <p className="mt-2 text-gray-500">
                  {otpRequested
                    ? `Enter the 6-digit OTP sent to ${maskedMobile || "your registered mobile"}.`
                    : "Enter your registered email or mobile number to receive an OTP."}
                </p>
              </div>

              {!otpRequested ? (
                <div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="forgotUsername"
                      name="username"
                      value={requestOtpFormik.values.username}
                      onChange={requestOtpFormik.handleChange}
                      onBlur={requestOtpFormik.handleBlur}
                      className={`block w-full pl-12 pr-4 py-3.5 bg-white border rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder-gray-400 ${
                        requestOtpFormik.touched.username && requestOtpFormik.errors.username
                          ? "border-red-300 focus:ring-red-500/20"
                          : "border-gray-200"
                      }`}
                      placeholder="Enter email or mobile"
                      autoComplete="username"
                    />
                  </div>
                  {requestOtpFormik.touched.username && requestOtpFormik.errors.username && (
                    <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {requestOtpFormik.errors.username}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Smartphone className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="otp"
                        name="otp"
                        value={resetPasswordFormik.values.otp}
                        onChange={resetPasswordFormik.handleChange}
                        onBlur={resetPasswordFormik.handleBlur}
                        className={`block w-full pl-12 pr-4 py-3.5 bg-white border rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder-gray-400 ${
                          resetPasswordFormik.touched.otp && resetPasswordFormik.errors.otp
                            ? "border-red-300 focus:ring-red-500/20"
                            : "border-gray-200"
                        }`}
                        placeholder="Enter 6-digit OTP"
                        inputMode="numeric"
                        maxLength={6}
                        autoComplete="one-time-code"
                      />
                    </div>
                    {resetPasswordFormik.touched.otp && resetPasswordFormik.errors.otp && (
                      <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {resetPasswordFormik.errors.otp}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="password"
                        id="newPassword"
                        name="newPassword"
                        value={resetPasswordFormik.values.newPassword}
                        onChange={resetPasswordFormik.handleChange}
                        onBlur={resetPasswordFormik.handleBlur}
                        className={`block w-full pl-12 pr-4 py-3.5 bg-white border rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder-gray-400 ${
                          resetPasswordFormik.touched.newPassword && resetPasswordFormik.errors.newPassword
                            ? "border-red-300 focus:ring-red-500/20"
                            : "border-gray-200"
                        }`}
                        placeholder="New password"
                      />
                    </div>
                    {resetPasswordFormik.touched.newPassword && resetPasswordFormik.errors.newPassword && (
                      <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {resetPasswordFormik.errors.newPassword}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={resetPasswordFormik.values.confirmPassword}
                        onChange={resetPasswordFormik.handleChange}
                        onBlur={resetPasswordFormik.handleBlur}
                        className={`block w-full pl-12 pr-4 py-3.5 bg-white border rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder-gray-400 ${
                          resetPasswordFormik.touched.confirmPassword && resetPasswordFormik.errors.confirmPassword
                            ? "border-red-300 focus:ring-red-500/20"
                            : "border-gray-200"
                        }`}
                        placeholder="Confirm new password"
                      />
                    </div>
                    {resetPasswordFormik.touched.confirmPassword && resetPasswordFormik.errors.confirmPassword && (
                      <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {resetPasswordFormik.errors.confirmPassword}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl transition-all transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {otpRequested ? "Reset Password" : "Send OTP"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerLogin;
